/**
 * iPhone hardware drawn around a mobile canvas: the black bezel, the titanium
 * band, the side buttons, and masks that round the screen's corners.
 *
 * Purely decorative and never part of the canvas data. It is laid over the
 * screen in the canvas's own coordinates, ignores the pointer and only paints
 * outward from the screen edge, so element positions, drag maths and saved
 * layouts are untouched. The corners are masked rather than clipped with
 * `overflow-hidden`, which keeps selection handles and element action buttons
 * near the screen edge usable in the builder.
 *
 * Shared by the builder canvas and the campaign-detail preview so the phone
 * looks identical while designing and while reviewing. The status bar, Dynamic
 * Island and home indicator are ordinary canvas elements, because their colour
 * depends on the screen behind them.
 */

import React from "react";

/**
 * How far the hardware reaches beyond the screen edge, in canvas pixels
 * (bezel + band + side buttons). Containers reserve this much space on every
 * side so the frame is never clipped or overlapped.
 */
export const PHONE_FRAME_REACH = 20;

interface PhoneFrameProps {
  /** Screen size in canvas pixels. */
  width: number;
  height: number;
  /** Scale applied to the canvas; the frame is drawn at canvas size and scaled with it. */
  scale?: number;
  className?: string;
}

// The screen radius matches the iPhone display's own corner radius (~47pt).
const CORNER = "absolute h-[47px] w-[47px]";

export const PhoneFrame: React.FC<PhoneFrameProps> = ({
  width,
  height,
  scale = 1,
  className = "",
}) => (
  <div
    aria-hidden="true"
    className={`pointer-events-none absolute left-0 top-0 origin-top-left ${className}`}
    style={{ width, height, transform: scale === 1 ? undefined : `scale(${scale})` }}
  >
    {/* Bezel, titanium band and drop shadow. Spread shadows follow the
        screen's border radius, so they trace the device outline exactly. */}
    <div className="absolute inset-0 rounded-[47px] shadow-[0_0_0_12px_#0B0B0C,0_0_0_14px_#5B5B60,0_0_0_15px_#1C1C1E,0_24px_60px_12px_rgba(0,0,0,0.28)]" />

    {/* Corner masks: bezel colour outside the rounded screen edge. */}
    <div className={`${CORNER} left-0 top-0 bg-[radial-gradient(circle_at_100%_100%,transparent_46.5px,#0B0B0C_47px)]`} />
    <div className={`${CORNER} right-0 top-0 bg-[radial-gradient(circle_at_0%_100%,transparent_46.5px,#0B0B0C_47px)]`} />
    <div className={`${CORNER} bottom-0 left-0 bg-[radial-gradient(circle_at_100%_0%,transparent_46.5px,#0B0B0C_47px)]`} />
    <div className={`${CORNER} bottom-0 right-0 bg-[radial-gradient(circle_at_0%_0%,transparent_46.5px,#0B0B0C_47px)]`} />

    {/* Side buttons: action button and volume rocker left, side button right. */}
    <div className="absolute -left-[18px] top-[118px] h-[32px] w-[3px] rounded-l-sm bg-[#48484C]" />
    <div className="absolute -left-[18px] top-[178px] h-[62px] w-[3px] rounded-l-sm bg-[#48484C]" />
    <div className="absolute -left-[18px] top-[252px] h-[62px] w-[3px] rounded-l-sm bg-[#48484C]" />
    <div className="absolute -right-[18px] top-[204px] h-[100px] w-[3px] rounded-r-sm bg-[#48484C]" />
  </div>
);
